import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  ElementRef,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { HelperService } from 'src/app/services/helper.service';
import { PermissionService } from 'src/app/services/permissions/permission.service';

/**
 * Generic "View Financial" gating directives, reusable across every module.
 *
 * Each module has a single {PREFIX}-VIEW-FINANCIAL permission (BIDS-VIEW-FINANCIAL,
 * PROPOSAL-VIEW-FINANCIAL, …). Pass the module's prefix and the directive derives the slug
 * and consults HelperService.isPermissionAvailable() (which already handles the super-admin
 * bypass + normalization).
 *
 *   [appFinancialBlur]="'BIDS'"     -> blurs the element when the user can't view financials
 *   *appFinancialVisible="'BIDS'"   -> removes the element from the DOM when they can't
 */

function financialSlug(modulePrefix: string): string {
  return (modulePrefix || '').toString().trim().toUpperCase() + '-VIEW-FINANCIAL';
}

/**
 * Attribute directive: blurs an inline financial value (adds `.financial-blurred`) when the
 * user lacks the module's View Financial permission. The real value is already redacted by
 * the backend; this just visually censors what's shown.
 */
@Directive({
  selector: '[appFinancialBlur]',
  standalone: false,
})
export class FinancialBlurDirective implements OnInit, OnDestroy {
  @Input('appFinancialBlur') modulePrefix = '';

  private permissionSub?: Subscription;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private helperService: HelperService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // No prefix => not gated (e.g. a shared component used in a non-financial context).
    if (!this.modulePrefix) {
      return;
    }
    // Re-evaluates when View Financial is granted or revoked mid-session.
    this.permissionSub = this.permissionService.permissions$.subscribe(() => {
      if (this.helperService.isPermissionAvailable(financialSlug(this.modulePrefix))) {
        this.renderer.removeClass(this.el.nativeElement, 'financial-blurred');
      } else {
        this.renderer.addClass(this.el.nativeElement, 'financial-blurred');
      }
    });
  }

  ngOnDestroy(): void {
    this.permissionSub?.unsubscribe();
  }
}

/**
 * Structural directive: renders the element only when the user has the module's View
 * Financial permission; otherwise it's removed from the DOM. Use for financial columns,
 * tabs, buttons, and charts.
 */
@Directive({
  selector: '[appFinancialVisible]',
  standalone: false,
})
export class FinancialVisibleDirective implements OnInit, OnDestroy {
  @Input('appFinancialVisible') modulePrefix = '';

  private permissionSub?: Subscription;
  /** Tracks what is rendered so we only tear down/rebuild on an actual flip. */
  private isRendered: boolean | null = null;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private helperService: HelperService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.permissionSub = this.permissionService.permissions$.subscribe(() => {
      // No prefix => not gated; otherwise render only when the user can View Financial.
      const shouldRender =
        !this.modulePrefix ||
        this.helperService.isPermissionAvailable(financialSlug(this.modulePrefix));

      // Re-creating the view on every emission would reset component state inside
      // the template (open menus, in-progress edits), so only act on a change.
      if (shouldRender === this.isRendered) {
        return;
      }
      this.isRendered = shouldRender;

      this.viewContainer.clear();
      if (shouldRender) {
        this.viewContainer.createEmbeddedView(this.templateRef);
      }
    });
  }

  ngOnDestroy(): void {
    this.permissionSub?.unsubscribe();
  }
}
